/**
 * マスタデータAPI実サービス
 *
 * @file masterdata.service.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * バックエンドマスタデータAPIとの実際の通信を行うサービス。
 * カテゴリー、為替レート、言語、通貨、国マスタを管理。
 */

import { Category, ID } from '@/types';
import { safeFetch } from '@shared/utils/api';

// APIベースURL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const API_V1_URL = `${API_BASE_URL}/api/v1`;

// バックエンドレスポンス型定義（backend/app/schemas/に対応）
interface BackendCategoryResponse {
  id: string;
  name: {
    ja: string;
    en: string;
    ko: string;
  };
  iconName?: string;
  slug?: string;
  parentId?: string;
  level: number;
  sortOrder: number;
  isActive: boolean;
  image?: string;
  createdAt: string;
  updatedAt: string;
}

interface BackendLanguageResponse {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BackendCurrencyResponse {
  id: string;
  code: string;
  name: string;
  symbol: string;
  exchange_rate: string; // Decimal型は文字列で送信される
  is_active: boolean;
  updated_at: string;
}

interface BackendCountryResponse {
  id: string;
  code: string;
  name: {
    ja: string;
    en: string;
    ko: string;
  };
  shipping_available: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// フロントエンド型定義
export interface Language {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  exchangeRate: number;
  isActive: boolean;
  updatedAt: Date;
}

export interface Country {
  id: string;
  code: string;
  name: {
    ja: string;
    en: string;
    ko: string;
  };
  shippingAvailable: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ExchangeRate {
  currency: string;
  rate: number;
  symbol: string;
  updatedAt: Date;
}

// バックエンド→フロントエンド型変換
const convertBackendCategoryToFrontend = (backendCategory: BackendCategoryResponse): Category => {
  return {
    id: backendCategory.id,
    name: backendCategory.name,
    icon: backendCategory.iconName,
    parentId: backendCategory.parentId,
    level: backendCategory.level,
    sortOrder: backendCategory.sortOrder,
    isActive: backendCategory.isActive,
    image: backendCategory.image,
    createdAt: new Date(backendCategory.createdAt),
    updatedAt: new Date(backendCategory.updatedAt)
  };
};

const convertBackendLanguageToFrontend = (backendLanguage: BackendLanguageResponse): Language => {
  return {
    id: backendLanguage.id,
    code: backendLanguage.code,
    name: backendLanguage.name,
    isActive: backendLanguage.is_active,
    createdAt: new Date(backendLanguage.created_at),
    updatedAt: new Date(backendLanguage.updated_at)
  };
};

const convertBackendCurrencyToFrontend = (backendCurrency: BackendCurrencyResponse): Currency => {
  return {
    id: backendCurrency.id,
    code: backendCurrency.code,
    name: backendCurrency.name,
    symbol: backendCurrency.symbol,
    exchangeRate: parseFloat(backendCurrency.exchange_rate),
    isActive: backendCurrency.is_active,
    updatedAt: new Date(backendCurrency.updated_at)
  };
};

const convertBackendCountryToFrontend = (backendCountry: BackendCountryResponse): Country => {
  return {
    id: backendCountry.id,
    code: backendCountry.code,
    name: backendCountry.name,
    shippingAvailable: backendCountry.shipping_available,
    isActive: backendCountry.is_active,
    createdAt: new Date(backendCountry.created_at),
    updatedAt: new Date(backendCountry.updated_at)
  };
};

// HTTPヘッダー生成
const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  return headers;
};

export const masterdataService = {
  /**
   * カテゴリー一覧取得
   * GET /api/v1/categories
   */
  getCategories: async (params?: {
    skip?: number;
    limit?: number;
    isActive?: boolean;
    parentId?: string;
    level?: number;
  }): Promise<Category[]> => {
    const queryParams = new URLSearchParams();

    if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.isActive !== undefined) queryParams.append('is_active', params.isActive.toString());
    if (params?.parentId) queryParams.append('parent_id', params.parentId);
    if (params?.level !== undefined) queryParams.append('level', params.level.toString());

    const url = `${API_V1_URL}/buyer/categories${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const backendCategories = await safeFetch<BackendCategoryResponse[]>(url, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'カテゴリー一覧取得に失敗しました'
    });

    if (!Array.isArray(backendCategories)) {
      throw new Error('カテゴリー一覧が配列ではありません。');
    }

    return backendCategories.map(convertBackendCategoryToFrontend);
  },

  /**
   * カテゴリー詳細取得
   * GET /api/v1/categories/:id
   */
  getCategoryById: async (id: ID): Promise<Category> => {
    const backendCategory = await safeFetch<BackendCategoryResponse>(`${API_V1_URL}/buyer/categories/${id}`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: `カテゴリー（ID: ${id}）の取得に失敗しました`
    });

    if (!backendCategory || typeof backendCategory !== 'object') {
      throw new Error('カテゴリーデータが不正です。');
    }

    return convertBackendCategoryToFrontend(backendCategory);
  },

  /**
   * 為替レート取得（通貨一覧から取得）
   * GET /api/v1/currencies
   */
  getExchangeRates: async (): Promise<ExchangeRate[]> => {
    const backendCurrencies = await safeFetch<BackendCurrencyResponse[]>(`${API_V1_URL}/currencies`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '為替レート取得に失敗しました'
    });

    if (!Array.isArray(backendCurrencies)) {
      throw new Error('為替レートデータが配列ではありません。');
    }

    return backendCurrencies
      .filter(c => c.is_active)
      .map(c => ({
        currency: c.code,
        rate: parseFloat(c.exchange_rate),
        symbol: c.symbol,
        updatedAt: new Date(c.updated_at)
      }));
  },

  /**
   * 言語一覧取得
   * GET /api/v1/languages
   */
  getLanguages: async (params?: { isActive?: boolean }): Promise<Language[]> => {
    const queryParams = new URLSearchParams();

    if (params?.isActive !== undefined) {
      queryParams.append('is_active', params.isActive.toString());
    }

    const url = `${API_V1_URL}/buyer/languages${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const backendLanguages = await safeFetch<BackendLanguageResponse[]>(url, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '言語一覧取得に失敗しました'
    });

    if (!Array.isArray(backendLanguages)) {
      throw new Error('言語一覧が配列ではありません。');
    }

    return backendLanguages.map(convertBackendLanguageToFrontend);
  },

  /**
   * 通貨一覧取得
   * GET /api/v1/currencies
   */
  getCurrencies: async (params?: { isActive?: boolean }): Promise<Currency[]> => {
    const queryParams = new URLSearchParams();

    if (params?.isActive !== undefined) {
      queryParams.append('is_active', params.isActive.toString());
    }

    const url = `${API_V1_URL}/buyer/currencies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const backendCurrencies = await safeFetch<BackendCurrencyResponse[]>(url, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '通貨一覧取得に失敗しました'
    });

    if (!Array.isArray(backendCurrencies)) {
      throw new Error('通貨一覧が配列ではありません。');
    }

    return backendCurrencies.map(convertBackendCurrencyToFrontend);
  },

  /**
   * 国一覧取得
   * GET /api/v1/countries
   */
  getCountries: async (params?: {
    isActive?: boolean;
    shippingAvailable?: boolean;
  }): Promise<Country[]> => {
    const queryParams = new URLSearchParams();

    if (params?.isActive !== undefined) {
      queryParams.append('is_active', params.isActive.toString());
    }
    if (params?.shippingAvailable !== undefined) {
      queryParams.append('shipping_available', params.shippingAvailable.toString());
    }

    const url = `${API_V1_URL}/buyer/countries${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const backendCountries = await safeFetch<BackendCountryResponse[]>(url, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '国一覧取得に失敗しました'
    });

    if (!Array.isArray(backendCountries)) {
      throw new Error('国一覧が配列ではありません。');
    }

    return backendCountries.map(convertBackendCountryToFrontend);
  }
};
